import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { getMerchDropStatus } from "../api/onboarding";
import { requestManualProducts } from "../actions/products";
import { setGlobalAlert } from "../actions/common";
import "../pages/listings/styles.scss";

export const MERCH_DROP_JOB_KEY = "riverr.merchDropJobs";
export const MERCH_DROP_JOB_EVENT = "riverr:merchDropJobs";
const POLL_MS = 3000;
// Client UI poll ceiling only — the server job keeps running after this.
// Scale with batch size (~45s/product budget, min 30m, max 6h).
const pollDeadlineMs = (total) => {
  const n = Math.max(1, Number(total) || 1);
  return Math.min(6 * 60 * 60 * 1000, Math.max(30 * 60 * 1000, n * 45 * 1000));
};

/**
 * App-level poller for bulk merch-drop create jobs.
 * Survives navigation so users can leave the bulk creator while products
 * create one-by-one in the background (same float pattern as single create).
 *
 * localStorage shape: { shopIds: string[], total: number, startedAt: number }
 */
const MerchDropJobWatcher = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pollTimerRef = useRef(null);
  const finishingRef = useRef(false);
  const jobRef = useRef(null);
  const [job, setJob] = useState(null);
  const [floatHidden, setFloatHidden] = useState(false);

  const onBulkCreate =
    location.pathname === "/products/bulk-create" ||
    location.pathname === "/products/bulk-create/";

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const clearStoredJob = () => {
    try {
      localStorage.removeItem(MERCH_DROP_JOB_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const readStoredJob = () => {
    try {
      const raw = localStorage.getItem(MERCH_DROP_JOB_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.shopIds?.length) return null;
      return {
        shopIds: parsed.shopIds.map(String),
        total: Number(parsed.total) || 0,
        startedAt: Number(parsed.startedAt) || Date.now(),
        status: "processing",
        completed: 0,
        created: 0,
        failed: 0,
      };
    } catch (_) {
      return null;
    }
  };

  const updateJob = (next) => {
    jobRef.current = next;
    setJob(next);
  };

  const finishJob = (status) => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    clearPoll();
    clearStoredJob();
    updateJob(status);

    const created = status?.created || 0;
    const failed = status?.failed || 0;
    const unpublished = status?.unpublished || 0;
    const total = status?.total || 0;
    const failureSamples = status?.failureSamples || [];

    dispatch(requestManualProducts({})).catch(() => {});

    if (status?.status === "failed" || (failed > 0 && created === 0)) {
      dispatch(
        setGlobalAlert({
          type: "error",
          message:
            (status?.error ||
              "Bulk product create failed. Check My Products and try again.") +
            (failureSamples[0] ? ` ${failureSamples[0]}` : ""),
        }),
      );
    } else if (failed > 0 || unpublished > 0) {
      const parts = [`${created} product${created !== 1 ? "s" : ""} created`];
      if (unpublished > 0) {
        parts.push(
          `${unpublished} created but not published`,
        );
      }
      if (failed > 0) parts.push(`${failed} failed`);
      dispatch(
        setGlobalAlert({
          type: "warning",
          message: `${parts.join(", ")}.${
            failureSamples[0] ? ` Example: ${failureSamples[0]}` : ""
          }`,
        }),
      );
    } else {
      dispatch(
        setGlobalAlert({
          type: "success",
          message: `${created || total || 0} product${
            (created || total) !== 1 ? "s" : ""
          } ready in My Products.`,
        }),
      );
    }

    setTimeout(() => {
      updateJob(null);
      finishingRef.current = false;
    }, 6000);
  };

  const pollJobs = async () => {
    const activeJob = jobRef.current;
    if (!activeJob?.shopIds?.length || activeJob.status !== "processing") {
      return;
    }
    try {
      const deadline = pollDeadlineMs(activeJob.total);
      if (Date.now() - (activeJob.startedAt || 0) > deadline) {
        // Stop the float only — server-side merchDropJobs keep compositing
        // and publishing; products still land in My Products as they finish.
        clearPoll();
        clearStoredJob();
        updateJob({
          ...activeJob,
          status: "completed",
          created: activeJob.created || 0,
          completed: activeJob.completed || 0,
        });
        dispatch(
          setGlobalAlert({
            type: "info",
            message:
              "Still creating products in the background. Check My Products — they’ll keep appearing as each one finishes.",
          }),
        );
        dispatch(requestManualProducts({})).catch(() => {});
        setTimeout(() => {
          updateJob(null);
          finishingRef.current = false;
        }, 8000);
        return;
      }

      const statuses = await Promise.all(
        activeJob.shopIds.map((shopId) => getMerchDropStatus(shopId)),
      );
      const drops = statuses.map((res) => res?.data?.merchDrop || res?.merchDrop);

      const completed = drops.reduce(
        (sum, d) => sum + (Number(d?.completed) || 0),
        0,
      );
      const created = drops.reduce(
        (sum, d) => sum + (Number(d?.created) || 0),
        0,
      );
      const failed = drops.reduce(
        (sum, d) => sum + (Number(d?.failed) || 0),
        0,
      );
      const unpublished = drops.reduce(
        (sum, d) => sum + (Number(d?.unpublished) || 0),
        0,
      );
      const failureSamples = drops.flatMap(
        (d) => d?.failureSamples || [],
      );
      const total =
        drops.reduce((sum, d) => sum + (Number(d?.total) || 0), 0) ||
        activeJob.total ||
        0;

      const settled = drops.every(
        (d) => d && ["completed", "failed"].includes(d.status),
      );
      const allFailed =
        settled && drops.every((d) => d?.status === "failed");

      const next = {
        ...activeJob,
        status: settled ? (allFailed ? "failed" : "completed") : "processing",
        completed,
        created,
        failed,
        unpublished,
        total,
        failureSamples,
      };

      if (settled) {
        finishJob(next);
        return;
      }

      updateJob(next);
      pollTimerRef.current = setTimeout(pollJobs, POLL_MS);
    } catch (_) {
      pollTimerRef.current = setTimeout(pollJobs, POLL_MS * 2);
    }
  };

  useEffect(() => {
    const stored = readStoredJob();
    if (stored) updateJob(stored);

    const onStarted = (event) => {
      const detail = event?.detail;
      if (!detail?.shopIds?.length) return;
      finishingRef.current = false;
      updateJob({
        shopIds: detail.shopIds.map(String),
        total: Number(detail.total) || 0,
        startedAt: Number(detail.startedAt) || Date.now(),
        status: "processing",
        completed: 0,
        created: 0,
        failed: 0,
      });
    };
    window.addEventListener(MERCH_DROP_JOB_EVENT, onStarted);
    return () => window.removeEventListener(MERCH_DROP_JOB_EVENT, onStarted);
  }, []);

  useEffect(() => {
    if (!job?.shopIds?.length || job.status !== "processing") {
      return clearPoll;
    }
    setFloatHidden(false);
    clearPoll();
    pollJobs();
    return clearPoll;
    // Only re-arm when a new job starts, not on every progress tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.shopIds?.join(","), job?.startedAt, job?.status === "processing"]);

  if (!job || floatHidden || onBulkCreate) return null;

  const isActive = job.status === "processing";
  const isFail = job.status === "failed";
  const isOk = job.status === "completed";
  if (!isActive && !isFail && !isOk) return null;

  const total = job.total || 0;
  const completed = Math.min(
    Number(job.completed) || 0,
    total > 0 ? total : Number(job.completed) || 0,
  );
  const percent =
    total > 0
      ? Math.min(95, Math.max(5, Math.round((completed / total) * 100)))
      : isOk
        ? 100
        : 15;

  const label = isFail
    ? "Bulk create failed"
    : isOk
      ? "Products ready"
      : completed > 0
        ? "Creating products…"
        : "Queued products…";

  return (
    <div
      className="listings-bulk-ignore-float"
      role="status"
      aria-live="polite"
      style={
        isFail
          ? { borderColor: "#f5c2c2" }
          : isOk
            ? { borderColor: "#b7e4c7" }
            : undefined
      }
    >
      <strong>{label}</strong>
      <div>
        {isActive &&
          (completed > 0
            ? `Creating ${completed} of ${total || "…"} in the background.`
            : `Waiting in queue — ${total || "…"} product${
                total === 1 ? "" : "s"
              } to create.`)}
        {isFail && (job.error || job.failureSamples?.[0] || "Something went wrong.")}
        {isOk &&
          `${job.created || total || 0} product${
            (job.created || total) !== 1 ? "s" : ""
          } in My Products.${
            job.failed
              ? ` ${job.failed} could not be created.`
              : ""
          }`}
      </div>
      {isActive && (
        <div className="listings-bulk-ignore-float__track">
          <div
            className="listings-bulk-ignore-float__fill"
            style={{
              width: `${percent}%`,
              animation: "none",
            }}
          />
        </div>
      )}
      <button
        type="button"
        className="listings-bulk-ignore-float__dismiss"
        onClick={() => setFloatHidden(true)}
      >
        Hide
      </button>
    </div>
  );
};

/** Persist active merch-drop job(s) so the watcher can resume after navigation. */
export function persistMerchDropJobs({ shopIds, total }) {
  const payload = {
    shopIds: (shopIds || []).map(String).filter(Boolean),
    total: Number(total) || 0,
    startedAt: Date.now(),
  };
  if (!payload.shopIds.length) return;
  try {
    localStorage.setItem(MERCH_DROP_JOB_KEY, JSON.stringify(payload));
  } catch (_) {
    /* ignore */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(MERCH_DROP_JOB_EVENT, { detail: payload }),
    );
  } catch (_) {
    /* ignore */
  }
}

export default MerchDropJobWatcher;
